import type {
  AccessContext,
  ILogger,
  ITelemetry,
  ModuleContext,
  ModuleManifest,
  ModuleSetup,
  Unsubscribe,
} from "@crialume/sdk";
import { parseOptions, z } from "../validation";
import { checkModuleAccess } from "./access";
import { ModuleManifestSchema } from "./manifest-schema";

export type ModuleStatus = "registered" | "loading" | "loaded" | "error";

export type RegistryEvent =
  | { type: "registered"; moduleId: string }
  | { type: "loading"; moduleId: string }
  | { type: "loaded"; moduleId: string; loadTimeMs: number }
  | { type: "unloaded"; moduleId: string }
  | { type: "error"; moduleId: string; error: Error };

/** Contexto escopado criado pelo kernel para cada módulo. */
export interface ScopedModuleContext {
  context: ModuleContext;
  dispose: () => void;
}

export interface ModuleRegistryOptions {
  createContext: (manifest: ModuleManifest) => ScopedModuleContext;
  logger: ILogger;
  telemetry: ITelemetry;
}

export interface RegisterResult {
  registered: string[];
  rejected: { source: string; reason: string }[];
}

interface ModuleEntry {
  manifest: ModuleManifest;
  status: ModuleStatus;
  setup?: ModuleSetup;
  scope?: ScopedModuleContext;
  error?: Error;
  loadTimeMs?: number;
}

/**
 * Registro de módulos — INSTÂNCIA por kernel (no CadFrn era singleton global
 * que fazia o próprio `import.meta.glob`, acoplado ao layout de pastas).
 * Quem descobre os manifestos é o shell; o registry só valida, decide acesso
 * e carrega sob demanda.
 */
export class ModuleRegistry {
  private readonly entries = new Map<string, ModuleEntry>();
  private readonly inflight = new Map<string, Promise<ModuleSetup>>();
  private readonly listeners = new Set<(event: RegistryEvent) => void>();
  private version = 0;

  constructor(private readonly options: ModuleRegistryOptions) {}

  register(manifests: readonly unknown[]): RegisterResult {
    const result: RegisterResult = { registered: [], rejected: [] };

    manifests.forEach((candidate, index) => {
      const source = readId(candidate) ?? `#${index}`;
      const parsed = ModuleManifestSchema.safeParse(candidate, parseOptions);
      if (!parsed.success) {
        result.rejected.push({ source, reason: z.prettifyError(parsed.error) });
        return;
      }
      // Mantém o objeto original (o parse do zod remove chaves desconhecidas).
      const manifest = candidate as ModuleManifest;
      if (this.entries.has(manifest.id)) {
        result.rejected.push({ source, reason: `id duplicado "${manifest.id}"` });
        return;
      }
      this.entries.set(manifest.id, { manifest, status: "registered" });
      result.registered.push(manifest.id);
      this.emit({ type: "registered", moduleId: manifest.id });
    });

    for (const { source, reason } of result.rejected) {
      this.options.logger.error(`manifesto rejeitado (${source}): ${reason}`);
    }
    return result;
  }

  get(id: string): ModuleManifest | undefined {
    return this.entries.get(id)?.manifest;
  }

  list(): ModuleManifest[] {
    return [...this.entries.values()].map((entry) => entry.manifest).sort(byOrder);
  }

  /**
   * Módulos que a sessão pode ver/abrir, já ordenados. Módulos com erro de
   * carga continuam listados: o usuário pode tentar de novo.
   */
  listAccessible(access: AccessContext): ModuleManifest[] {
    return this.list().filter((manifest) => checkModuleAccess(manifest, access).allowed);
  }

  getStatus(id: string): ModuleStatus | undefined {
    return this.entries.get(id)?.status;
  }

  getContext(id: string): ModuleContext | undefined {
    return this.entries.get(id)?.scope?.context;
  }

  /** Muda a cada evento — útil como snapshot para `useSyncExternalStore`. */
  getVersion(): number {
    return this.version;
  }

  async load(id: string): Promise<ModuleSetup> {
    return this.loadWithTrail(id, []);
  }

  async unload(id: string): Promise<void> {
    const entry = this.entries.get(id);
    if (!entry || entry.status !== "loaded") return;
    try {
      await entry.setup?.dispose?.();
    } finally {
      entry.scope?.dispose();
      entry.status = "registered";
      entry.setup = undefined;
      entry.scope = undefined;
      this.emit({ type: "unloaded", moduleId: id });
    }
  }

  subscribe(listener: (event: RegistryEvent) => void): Unsubscribe {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Diagnóstico: `window.__crialume.registry.snapshot()` no console. */
  snapshot() {
    return [...this.entries.values()].map(({ manifest, status, loadTimeMs, error }) => ({
      id: manifest.id,
      version: manifest.version,
      status,
      loadTimeMs,
      error: error?.message,
    }));
  }

  private async loadWithTrail(id: string, trail: string[]): Promise<ModuleSetup> {
    if (trail.includes(id)) {
      throw new Error(`Dependência circular: ${[...trail, id].join(" → ")}`);
    }
    const entry = this.entries.get(id);
    if (!entry) throw new Error(`Módulo "${id}" não registrado.`);
    if (entry.status === "loaded" && entry.setup) return entry.setup;

    const pending = this.inflight.get(id);
    if (pending) return pending;

    const promise = this.performLoad(entry, [...trail, id]).finally(() => {
      this.inflight.delete(id);
    });
    this.inflight.set(id, promise);
    return promise;
  }

  private async performLoad(entry: ModuleEntry, trail: string[]): Promise<ModuleSetup> {
    const { manifest } = entry;
    const { logger, telemetry } = this.options;
    const startedAt = performance.now();

    entry.status = "loading";
    entry.error = undefined;
    this.emit({ type: "loading", moduleId: manifest.id });

    let scope: ScopedModuleContext | undefined;
    try {
      for (const dependency of manifest.requirements?.dependencies ?? []) {
        await this.loadWithTrail(dependency, trail);
      }

      const exported = await manifest.loader();
      const factory = exported?.default;
      if (typeof factory !== "function") {
        throw new Error(`"${manifest.id}" deve ter export default de defineModule(...).`);
      }

      scope = this.options.createContext(manifest);
      const setup = factory(scope.context);
      if (!setup || !Array.isArray(setup.routes)) {
        throw new Error(`"${manifest.id}": a factory deve retornar { routes: [...] }.`);
      }
      await setup.init?.();

      const loadTimeMs = performance.now() - startedAt;
      Object.assign(entry, { status: "loaded", setup, scope, loadTimeMs });
      logger.info(
        `módulo ${manifest.id}@${manifest.version} carregado em ${loadTimeMs.toFixed(0)}ms`,
      );
      telemetry.trackMetric("module_load_ms", loadTimeMs, { moduleId: manifest.id });
      this.emit({ type: "loaded", moduleId: manifest.id, loadTimeMs });
      return setup;
    } catch (cause) {
      scope?.dispose();
      const error = cause instanceof Error ? cause : new Error(String(cause));
      Object.assign(entry, { status: "error", error, setup: undefined, scope: undefined });
      logger.error(`falha ao carregar ${manifest.id}`, error);
      telemetry.trackError(error, { moduleId: manifest.id, phase: "load" });
      this.emit({ type: "error", moduleId: manifest.id, error });
      throw error;
    }
  }

  private emit(event: RegistryEvent): void {
    this.version++;
    for (const listener of [...this.listeners]) {
      try {
        listener(event);
      } catch (error) {
        this.options.logger.error("listener do registry falhou", error);
      }
    }
  }
}

function byOrder(a: ModuleManifest, b: ModuleManifest): number {
  return (a.meta.order ?? 100) - (b.meta.order ?? 100) || a.meta.name.localeCompare(b.meta.name);
}

function readId(candidate: unknown): string | undefined {
  const id = (candidate as { id?: unknown } | null)?.id;
  return typeof id === "string" ? id : undefined;
}
